import type { orpcClient } from "@shared/lib/orpc";
import { orpc } from "@shared/lib/orpc";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { z } from "zod";

/** Where shoppers' apps, sites and servers reach Hephon: this site (SITE_URL), no trailing slash. */
export const SITE_ORIGIN = (
	(import.meta.env["VITE_SITE_URL"] as string | undefined) ?? ""
).replace(/\/$/, "");

/** The Install picker's platforms, in picker order (SDKS.md 7.1). */
export const PLATFORM_IDS = [
	"flutter",
	"react-native",
	"ios",
	"android",
	"web",
	"api",
] as const;
export type PlatformId = (typeof PLATFORM_IDS)[number];

/** Links from before the platform picker said `?tab=app|web|api`; the app tab was Flutter. */
const LEGACY_TAB: Record<string, PlatformId> = {
	app: "flutter",
	web: "web",
	api: "api",
};

/** A platform's two views: the steps to install it, and what to look up afterwards. */
export const INSTALL_VIEWS = ["setup", "reference"] as const;
export type InstallView = (typeof INSTALL_VIEWS)[number];

export interface InstallSearch {
	platform?: PlatformId;
	method?: string;
	view?: InstallView;
}

/**
 * `?platform=<id>&method=<id>&view=reference`. An old `?tab=` link opens its platform; an unknown value opens
 * the default instead of an error. The method is checked against the platform on screen.
 */
export const installSearchSchema = z.preprocess(
	(raw) => {
		if (typeof raw !== "object" || raw === null) {
			return raw;
		}
		const { tab, ...search } = raw as Record<string, unknown>;
		return typeof tab === "string" && !("platform" in search)
			? { ...search, platform: LEGACY_TAB[tab] }
			: search;
	},
	z.object({
		platform: z.enum(PLATFORM_IDS).optional().catch(undefined),
		method: z
			.string()
			.regex(/^[a-z-]{1,40}$/)
			.optional()
			.catch(undefined),
		view: z.enum(INSTALL_VIEWS).optional().catch(undefined),
	}),
);

/**
 * The route's `validateSearch`. A preprocess schema takes `unknown`, so this states what links
 * may pass: old `?tab=` links still parse, but new code can only link with platform and method.
 */
export function validateInstallSearch(
	search: InstallSearch & SearchSchemaInput,
): InstallSearch {
	return installSearchSchema.parse(search);
}

export type LaunchKey = Awaited<
	ReturnType<typeof orpcClient.embedSettings.launchKeys.list>
>["keys"][number];

/** One SDK's releases from `sdkReleases.list`, newest first. */
export type SdkReleaseSummary = Awaited<
	ReturnType<typeof orpcClient.sdkReleases.list>
>["platforms"][number];

/** One surface's install status from `embedSettings.installStatus`. */
export type SurfaceInstall = Awaited<
	ReturnType<typeof orpcClient.embedSettings.installStatus>
>["surfaces"][number];
export type InstallSurface = SurfaceInstall["surface"];

export function embedSettingsQuery(organizationId: string) {
	return orpc.embedSettings.get.queryOptions({ input: { organizationId } });
}

export function launchKeysQuery(organizationId: string) {
	return orpc.embedSettings.launchKeys.list.queryOptions({
		input: { organizationId },
	});
}

export function sdkReleasesQuery() {
	return orpc.sdkReleases.list.queryOptions();
}

export function installStatusQuery(organizationId: string) {
	return orpc.embedSettings.installStatus.queryOptions({
		input: { organizationId },
	});
}
